import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../lib/authSlice';
import {
  getImpersonation, endImpersonation, impersonationLabel, IMPERSONATION_EVENT,
} from '../lib/impersonation';

/**
 * "You are acting as someone else" — the safety rail for impersonation.
 *
 * Loud on purpose. The one genuinely dangerous failure mode is forgetting you
 * are impersonating and treating a customer's account as your own, so this is
 * a full-width amber bar that never collapses and cannot be dismissed except
 * by actually switching back.
 *
 * It also counts down: the session dies with its token, and an admin who knows
 * they have four minutes left behaves differently than one who doesn't.
 */
export default function ImpersonationBanner() {
  const [session, setSession] = useState(() => getImpersonation());
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Re-read every 30s so an expired session stops claiming to be live, and so
  // the countdown stays honest without a per-second timer.
  useEffect(() => {
    const sync = () => {
      setNow(Date.now());
      setSession(getImpersonation());
    };
    // A switch announces itself rather than leaving the bar to wait for the
    // next poll; `storage` covers the admin open in another tab.
    window.addEventListener(IMPERSONATION_EVENT, sync);
    window.addEventListener('storage', sync);
    const t = setInterval(sync, 30000);
    return () => {
      window.removeEventListener(IMPERSONATION_EVENT, sync);
      window.removeEventListener('storage', sync);
      clearInterval(t);
    };
  }, []);

  const switchBack = useCallback(async () => {
    setBusy(true);
    try {
      const admin = await endImpersonation();
      setSession(null);
      dispatch(setUser(admin || undefined));
      // Land back where you'd want to be: the user you were just acting as.
      const target = session && session.target;
      navigate(admin && target ? `/admin/users/${target.id}` : '/');
    } catch (e) {
      setBusy(false);
    }
  }, [dispatch, navigate, session]);

  if (!session) return null;

  const label = impersonationLabel(session.target);
  const minutesLeft = session.expiresAt
    ? Math.max(0, Math.round((new Date(session.expiresAt).getTime() - now) / 60000))
    : null;
  // A link session was opened in a window that was never signed in as an admin,
  // so there is no account to return to — ending it signs this window out.
  const isLink = session.mode === 'link';

  return (
    <div className="w-full bg-amber-500 text-amber-950 print:hidden">
      <div className="flex items-center gap-3 px-4 py-2 text-sm">
        <svg className="h-5 w-5 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.5 0l-7.1 12.25A2 2 0 004.99 19z" />
        </svg>
        <span className="min-w-0 flex-1">
          <strong className="font-semibold">Acting as {label}</strong>
          <span className="hidden sm:inline">
            {' '}— everything you change here is recorded as them, with your name attached.
          </span>
          {minutesLeft !== null && (
            <span className="ml-1 opacity-80">
              Ends in {minutesLeft} min.
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={switchBack}
          disabled={busy}
          className="flex-none rounded-md bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-900 disabled:opacity-60"
        >
          {busy ? 'Ending…' : (isLink ? 'End session' : 'Switch back')}
        </button>
      </div>
    </div>
  );
}
